/**
 * CommunityReplyBar — the small reply-context bar shown above the composer
 * input when the user is replying to a message on the community wall.
 *
 * Flutter has no dedicated reply bar: the reply context surfaces only as the
 * composer hint (`"Reply to <userName>"`, `community_screen.dart:1287`), and the
 * reply is cleared by tapping the dimming overlay
 * (`addingMessageOverlay` → `_messageToReplyNotifier.value = null`,
 * `community_screen.dart:1402-1406`). On the web a desktop viewport has no
 * tap-overlay affordance, so this small bar (author + snippet + an explicit
 * dismiss control) is the web-equivalent way to show and dismiss the reply
 * target. It is a UI-only addition — the business semantics (which target is
 * set, how it clears) are unchanged and owned by `useCommunity.setReplyTarget`.
 *
 * Rendered only when a reply target is set; the parent composer mounts it
 * conditionally.
 *
 * Theme / responsive tokens:
 *  - Bar background → `theme.surface`; author → `theme.exclusive` (matches the
 *    reply accent), snippet → `theme.text.secondary`.
 *  - Dismiss icon → `close.png` via `AssetIcon`, tinted `theme.icon.secondary`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface CommunityReplyBarProps {
  /** The message currently being replied to. */
  messageToReply: CommunityChatMessageEntity;
  /** Clears the reply target — wired by the composer to `setReplyTarget(null)`. */
  onDismiss: () => void;
}

export const CommunityReplyBar: React.FC<CommunityReplyBarProps> = ({
  messageToReply,
  onDismiss,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Author of the replied message — mirrors the hint's
  // `messageToReply.messageUsersInfo.userName` (`community_screen.dart:1287`).
  const authorName = messageToReply.messageUsersInfo.userName;
  // Snippet of the replied text (the main replied message body).
  const snippet = messageToReply.text;

  return (
    <div
      {...qaAttr({ id: 'community-reply-bar' })}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: responsive.gap.sm,
        backgroundColor: theme.surface,
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        borderTop: `${responsive.border.thin}px solid ${theme.border}`,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: responsive.spacing.xxs,
        }}
      >
        <span
          style={{
            color: theme.exclusive,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.semibold,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {l10n.community.composerHintReply.replace('{name}', authorName)}
        </span>
        <span
          style={{
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {snippet}
        </span>
      </div>

      <button
        {...qaAttr({ id: 'community-reply-dismiss' })}
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AssetIcon
          path="/assets/images/close.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.icon.secondary}
        />
      </button>
    </div>
  );
};
